import React from 'react';
import PropTypes from 'prop-types';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Box, CircularProgress, Typography } from '@mui/material';

/**
 * Standardized loading component for dashboard pages
 * @param {Object} props
 * @param {string} [props.message] - Optional loading message (will be localized)
 * @param {boolean} [props.showSpinner=true] - Whether to show the spinner
 * @param {string} [props.messageKey] - Localization key for the message
 */
export const DashboardLoading = ({
  message,
  messageKey = 'generic.loadingResults',
  showSpinner = true,
}) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
    {showSpinner && <CircularProgress sx={{ marginRight: 2 }} />}
    <Typography>{message || LanguageConverterV2(messageKey)}</Typography>
  </Box>
);

DashboardLoading.propTypes = {
  message: PropTypes.string,
  messageKey: PropTypes.string,
  showSpinner: PropTypes.bool,
};

/**
 * Inline loading component for smaller loading states
 * @param {Object} props
 * @param {string} [props.size] - Size of the spinner ('small' | 'medium' | 'large')
 */
export const InlineLoading = ({ size = 'medium' }) => {
  let spinnerSize = 24; // default medium
  if (size === 'small') spinnerSize = 20;
  if (size === 'large') spinnerSize = 40;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <CircularProgress size={spinnerSize} />
    </Box>
  );
};

InlineLoading.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large']),
};

/**
 * Loading component for no data states
 * @param {Object} props
 * @param {string} [props.messageKey] - Localization key for the no data message
 */
export const NoDataState = ({ messageKey = 'dashboard.noDataAvailable' }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
    <Typography>{LanguageConverterV2(messageKey)}</Typography>
  </Box>
);

NoDataState.propTypes = {
  messageKey: PropTypes.string,
};

/**
 * Loading component for access denied states
 * @param {Object} props
 * @param {string} [props.messageKey] - Localization key for the access denied message
 */
export const AccessDeniedState = ({ messageKey = 'dashboard.accessDenied' }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
    <Typography>{LanguageConverterV2(messageKey)}</Typography>
  </Box>
);

AccessDeniedState.propTypes = {
  messageKey: PropTypes.string,
};

export default {
  DashboardLoading,
  InlineLoading,
  NoDataState,
  AccessDeniedState,
};
